import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { mentionType } from '@aline/common';
import { IconButton, Paper, Portal, Skeleton, Typography } from '@mui/material';
import { useComboboxInput, useHTMLInputCursorState } from '@platejs/combobox/react';
import { MentionInputPlugin, MentionPlugin } from '@platejs/mention/react';
import {
  ArrowLeft,
  BookOpenText,
  ChevronRight,
  ClipboardList,
  FileText,
  Plus,
  Sparkle,
  Table2,
  UserRound,
  Workflow,
} from 'lucide-react';
import { TextApi } from 'platejs';
import type { Point, TElement } from 'platejs';
import type { PlateElementProps } from 'platejs/react';
import { toPlatePlugin, useEditorRef } from 'platejs/react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { usePopper } from 'react-popper';
import {
  AlineAvatar,
  alineAvatarSizes,
  AlineCheckbox,
  SmallAlineButton,
  StorableIcon,
} from 'src/components';
import { UploadFilesPicker } from 'src/components/FilestackPicker';
import { Apollo } from 'src/gql/generated';
import { composeRefs } from 'src/helpers/compose-refs';
import { aiChatAcceptedMimeTypes, FileStackDomContainer } from 'src/helpers/integrations/filestack';
import { makeStyles } from 'tss-react/mui';
import { useDebounce } from 'use-debounce';

import { BaseMentionElementPlugin } from './mention-element-plugin';
import {
  DEFAULT_ENABLED_TYPES,
  filterUserResults,
  getDocumentMentionDisplayName,
  getIsMultiSelectSearchType,
  getMentionEntitySearchCursorInput,
  getMultiSelectResultForItem,
  getPickerItems,
  getSearchMentionEntitiesInput,
  getSearchResultDisplayName,
  getSelectedEntityKey,
  getShouldCommitPendingSelection,
  toggleSelectedEntity,
} from './mention-entity-picker-helpers';
import type {
  MultiSelectSearchResult,
  PickerItem,
  SearchResult,
} from './mention-entity-picker-helpers';
import { MENTION_TRIGGER_PREVIOUS_CHAR_PATTERN } from '../../ChatEditor/ChatEditorMention/mention-trigger';
import { MentionElement } from '../../ChatEditor/ChatEditorMention/MentionElement';
import type {
  AlineAiMentionElement,
  DocumentMentionElement,
  EmailMentionElement,
  IntakeFormMentionElement,
  PlaybookMentionElement,
  ReportMentionElement,
  TMentionElement,
  UserMentionElement,
  WorkflowMentionElement,
} from '../../ChatEditor/ChatEditorMention/utils';
import { MAX_CHAT_UPLOAD_FILES } from '../../ChatEditor/utils';
import type { MentionEntitiesConfig } from '../types';

const ALINE_AI_OPTION_KEY = 'aline-ai';
const ALINE_AI_OPTION_LABEL = 'Aline AI';
const INTAKE_FORM_OPTION_LABEL = 'Intake Form';
export const ALINE_MARKDOWN_MENTION_MENU_ATTR = 'data-aline-markdown-mention-menu';

type PickerKeyboardEvent = KeyboardEvent | React.KeyboardEvent<HTMLInputElement>;

const CATEGORY_LABELS = {
  [Apollo.MentionEntitySearchType.Document]: 'Documents',
  [Apollo.MentionEntitySearchType.Playbook]: 'Playbooks',
  [Apollo.MentionEntitySearchType.Report]: 'Reports',
  [Apollo.MentionEntitySearchType.User]: 'Users',
  [Apollo.MentionEntitySearchType.Workflow]: 'Workflows',
} satisfies Record<Apollo.MentionEntitySearchType, string>;

export const MentionEntitiesContext = createContext<MentionEntitiesConfig | null>(null);

export function useMentionEntitiesContext(): MentionEntitiesConfig {
  const ctx = useContext(MentionEntitiesContext);
  if (!ctx)
    throw new Error('useMentionEntitiesContext must be used within MentionEntitiesContext.');
  return ctx;
}

export const MentionElementPlugin =
  toPlatePlugin(BaseMentionElementPlugin).withComponent(MentionElement);

export const MentionEntityKit = [
  MentionPlugin.configure({
    options: {
      triggerPreviousCharPattern: MENTION_TRIGGER_PREVIOUS_CHAR_PATTERN,
    },
  }).withComponent(MentionElement),
  MentionInputPlugin.configure({
    node: {
      isInline: true,
      isVoid: false,
    },
  }).withComponent(MentionEntityInputElement),
];

const useStyles = makeStyles()(theme => ({
  categoryItemContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    minWidth: 1,
    width: '100%',
  },
  disabledItem: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    borderRadius: 4,
    color: theme.palette.text.disabled,
    cursor: 'default',
    margin: theme.spacing(0.25, 0.5),
    padding: theme.spacing(0.5, 0.75),
  },
  drilldownEmpty: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    padding: theme.spacing(1.5, 2),
    textAlign: 'center',
  },
  drilldownHeader: {
    alignItems: 'center',
    backgroundColor: theme.palette.grey[100],
    boxSizing: 'border-box',
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: 12,
    fontWeight: 700,
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.25, 1),
    position: 'sticky',
    textTransform: 'uppercase',
    top: 0,
    width: '100%',
    zIndex: 1,
  },
  drilldownHeaderButton: {
    color: 'inherit',
    height: 20,
    padding: 0,
    width: 20,
  },
  emailTitleDisabled: {
    color: theme.palette.text.disabled,
  },
  emptyState: {
    padding: theme.spacing(0.5, 1),
  },
  footer: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1),
    width: '100%',
  },
  groupHeader: {
    alignItems: 'center',
    backgroundColor: theme.palette.grey[100],
    boxSizing: 'border-box',
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: 12,
    fontWeight: 700,
    padding: theme.spacing(0.5, 1),
    position: 'sticky',
    textTransform: 'uppercase',
    top: 0,
    width: '100%',
    zIndex: 1,
  },
  hiddenInput: {
    background: 'transparent',
    border: 'none',
    caretColor: theme.palette.text.primary,
    color: 'inherit',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    left: 0,
    /**
     * The mirror inherits these from the editor but the browser stylesheet resets them on an
     * input, so they are re-inherited to keep the two measuring the same text the same way.
     */
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    margin: 0,
    minWidth: 0,
    outline: 'none',
    padding: 0,
    position: 'absolute',
    textTransform: 'inherit',
    top: 0,
    // Overflows the mirror by a pixel so the caret is drawable without widening the span.
    width: 'calc(100% + 1px)',
    wordSpacing: 'inherit',
  },
  highlightedItem: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    backgroundColor: theme.appColors.selectedBackground,
    borderRadius: 4,
    cursor: 'pointer',
    margin: theme.spacing(0.25, 0.5),
    padding: theme.spacing(0.5, 0.75),
  },
  /**
   * Sizes the search input to exactly the text it holds: the input is absolutely positioned
   * over a hidden mirror of its own value, so the span is exactly as wide as the text. A
   * character-count estimate would leave a visible gap before whatever follows the mention
   * input, which shows up whenever the picker is opened inside existing text.
   */
  inputSizer: {
    '&::after': {
      // The zero-width space keeps a full-height line box while the search text is empty.
      content: 'attr(data-value) "\\200b"',
      font: 'inherit',
      visibility: 'hidden',
      whiteSpace: 'pre',
    },
    display: 'inline-block',
    // The input is absolutely positioned, so the hidden mirror text alone sets the width.
    position: 'relative',
  },
  item: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    borderRadius: 4,
    cursor: 'pointer',
    margin: theme.spacing(0.25, 0.5),
    padding: theme.spacing(0.5, 0.75),
  },
  itemCheckbox: {
    flexShrink: 0,
    padding: 0,
  },
  itemContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  itemText: {
    display: 'flex',
    minWidth: 0,
  },
  mentionIcon: {
    alignItems: 'center',
    backgroundColor: 'rgba(20, 110, 232, 0.12)',
    borderRadius: '50%',
    color: theme.appColors.primary,
    display: 'flex',
    flexShrink: 0,
    fontSize: 16,
    fontWeight: 700,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  plusIcon: {
    alignItems: 'center',
    backgroundColor: theme.appColors.primary,
    borderRadius: '50%',
    color: theme.palette.common.white,
    display: 'flex',
    flexShrink: 0,
    height: alineAvatarSizes.xs.size,
    justifyContent: 'center',
    width: alineAvatarSizes.xs.size,
  },
  plusIconDisabled: {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'min(340px, calc(100vh - 24px))',
    maxWidth: 'calc(100vw - 16px)',
    overflow: 'hidden',
    padding: 0,
    width: 340,
    zIndex: theme.zIndex.modal - 1,
  },
  rowIcon: {
    alignItems: 'center',
    color: theme.appColors.secondaryType,
    display: 'flex',
    flexShrink: 0,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  /**
   * Independent scrollport so the Insert footer can sit below this region as a flex sibling.
   * Sticky `bottom: 0` on a last child of the same overflow container only pins after the
   * user scrolls to the end — which hides Insert on a Documents drill-down taller than 340px.
   */
  scrollBody: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  section: {
    width: '100%',
  },
  selectableItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
  },
  selectableItemContent: {
    flex: 1,
    minWidth: 0,
  },
  skeletonText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  uploadButton: {
    fontSize: 10,
    fontWeight: 600,
    marginLeft: 'auto',
  },
}));

export function MentionEntityInputElement(props: PlateElementProps<TElement>): JSX.Element {
  const editor = useEditorRef();
  const { classes } = useStyles();
  const config = useMentionEntitiesContext();
  const { attributes, children, element } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const insertPoint = useRef<Point | null>(null);
  const [searchText, setSearchText] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const cursorState = useHTMLInputCursorState(inputRef);
  const mentionSearchText = getMentionInputSearchText(element, searchText);
  const [debouncedSearchText] = useDebounce(mentionSearchText, 250);
  const enabledTypes = config.enabledTypes ?? DEFAULT_ENABLED_TYPES;
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [highlightedItemKey, setHighlightedItemKey] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<Apollo.MentionEntitySearchType | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<MultiSelectSearchResult[]>([]);
  /**
   * The upload picker is a portaled dialog, so its clicks/keys land outside the popper.
   * Keep a ref the document-level close handlers can read to avoid tearing down the
   * mention input (and thus the dialog) while an upload is in progress.
   */
  const uploadOpenRef = useRef(false);
  uploadOpenRef.current = uploadOpen;
  const queryMode = getQueryMode({ activeType, query: mentionSearchText });
  const quickTypes = config.quickTypes;
  const queryInput = getSearchMentionEntitiesInput({
    activeType,
    documentId: config.documentId,
    enabledTypes,
    query: debouncedSearchText,
    quickTypes,
    workflowId: config.workflowId,
  });
  const { data, error, fetchMore, loading } = Apollo.useSearchMentionEntitiesQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: { input: queryInput },
  });
  const groups = useMemo(
    () => filterUserResults(data?.searchMentionEntities.groups ?? [], config.filterUser),
    [config.filterUser, data]
  );
  const items = useMemo(
    () =>
      getPickerItems({
        activeType,
        allowAlineAiMention: Boolean(config.allowAlineAiMention),
        allowEmailMentions: Boolean(config.allowEmailMentions),
        allowFileUpload: Boolean(config.allowFileUpload),
        allowIntakeFormMention: Boolean(config.allowIntakeFormMention),
        enabledTypes,
        groups,
        query: mentionSearchText,
        quickTypes,
      }),
    [
      activeType,
      config.allowAlineAiMention,
      config.allowEmailMentions,
      config.allowFileUpload,
      config.allowIntakeFormMention,
      enabledTypes,
      groups,
      mentionSearchText,
      quickTypes,
    ]
  );
  const optionKeys = useMemo(
    () => items.filter(item => !getIsPickerItemDisabled(item)).map(item => item.key),
    [items]
  );
  const activeGroup = activeType ? (groups.find(group => group.type === activeType) ?? null) : null;
  const selectedEntityKeys = useMemo(
    () => new Set(selectedEntities.map(getSelectedEntityKey)),
    [selectedEntities]
  );
  /**
   * Keep the Insert footer on every screen of a picker that can check off entities, including
   * Users drill-down, so a pending selection is never left waiting out of sight. User-only
   * surfaces (comments) skip it because they have nothing to batch.
   */
  const showSelectionFooter =
    enabledTypes.some(getIsMultiSelectSearchType) || Boolean(selectedEntities.length);
  const scrollableTargetId = `mention-entity-scroll-${activeType ?? queryMode}`;
  /**
   * Pick a stable opening direction from the trigger's viewport position: editors in the bottom
   * half open upward, editors in the top half open downward. We disable Popper's flip modifier
   * below so it doesn't keep re-deciding placement while the user types or the menu content changes.
   */
  const preferredPlacement =
    anchorEl &&
    typeof window !== 'undefined' &&
    anchorEl.getBoundingClientRect().top > window.innerHeight / 2
      ? 'top-start'
      : 'bottom-start';
  const { attributes: popperAttributes, styles: popperStyles } = usePopper(
    anchorEl,
    popperElement,
    {
      modifiers: [
        { name: 'offset', options: { offset: [0, 4] } },
        {
          enabled: false,
          name: 'flip',
        },
        {
          name: 'preventOverflow',
          options: {
            altAxis: true,
            padding: 8,
            rootBoundary: 'viewport',
            tether: false,
          },
        },
      ],
      placement: preferredPlacement,
      strategy: 'fixed',
    }
  );

  const reinsertCancelledInput = useCallback(() => {
    editor.tf.insertText(`@${mentionSearchText}`, { at: insertPoint.current ?? undefined });
  }, [editor, mentionSearchText]);

  const { props: inputProps, removeInput } = useComboboxInput({
    cancelInputOnArrowLeftRight: false,
    cancelInputOnBlur: false,
    cancelInputOnEscape: true,
    cursorState,
    onCancelInput: cause => {
      if (cause !== 'backspace') reinsertCancelledInput();
      if (cause === 'arrowLeft' || cause === 'arrowRight') {
        editor.tf.move({
          distance: 1,
          reverse: cause === 'arrowLeft',
        });
      }
    },
    ref: inputRef,
  });

  const closeInputWithoutText = useCallback(() => {
    const point = insertPoint.current;
    removeInput(false);
    if (point) editor.tf.select(point);
  }, [editor, removeInput]);

  const selectAlineAi = useCallback(() => {
    removeInput(true);
    const mentionNode: AlineAiMentionElement = {
      children: [{ text: '' }],
      mentionTypeName: 'aline-ai',
      type: mentionType,
      value: ALINE_AI_OPTION_KEY,
    };
    editor.tf.insertNodes([mentionNode, { text: ' ' }], { select: true });
  }, [editor, removeInput]);

  const selectSearchResult = useCallback(
    (result: SearchResult) => {
      const mentionNode = getMentionNodeForResult(result);
      if (!mentionNode) return;

      removeInput(true);
      editor.tf.insertNodes([mentionNode, { text: ' ' }], { select: true });
    },
    [editor, removeInput]
  );

  const selectEmail = useCallback(
    (email: string) => {
      const mentionNode: EmailMentionElement = {
        children: [{ text: '' }],
        email,
        mentionTypeName: 'email',
        type: mentionType,
        value: email,
      };

      removeInput(true);
      editor.tf.insertNodes([mentionNode, { text: ' ' }], { select: true });
    },
    [editor, removeInput]
  );

  const selectIntakeForm = useCallback(() => {
    const mentionNode: IntakeFormMentionElement = {
      children: [{ text: '' }],
      mentionTypeName: 'intake-form',
      type: mentionType,
      value: INTAKE_FORM_OPTION_LABEL,
    };

    removeInput(true);
    editor.tf.insertNodes([mentionNode, { text: ' ' }], { select: true });
  }, [editor, removeInput]);

  const insertMentionElements = useCallback(
    (mentionNodes: TMentionElement[]) => {
      const nodes = mentionNodes.flatMap(mentionNode => [mentionNode, { text: ' ' }]);
      if (!nodes.length) return;

      removeInput(true);
      editor.tf.insertNodes(nodes, { select: true });
    },
    [editor, removeInput]
  );

  const toggleEntitySelection = useCallback((result: MultiSelectSearchResult) => {
    setSelectedEntities(prev => toggleSelectedEntity(prev, result));
  }, []);

  const insertSelectedEntities = useCallback(() => {
    insertMentionElements(getMentionNodesForResults(selectedEntities));
  }, [insertMentionElements, selectedEntities]);

  /**
   * Drill-down navigation deliberately keeps the pending selection: entities can be checked off
   * in the preview list and in a type drill-down, and moving between the two must not discard
   * what is already checked.
   */
  const openDrilldown = useCallback((type: Apollo.MentionEntitySearchType) => {
    setActiveType(type);
    setHighlightedItemKey(null);
  }, []);

  const closeDrilldown = useCallback(() => {
    setActiveType(null);
    setHighlightedItemKey(null);
  }, []);

  const selectItem = useCallback(
    (item: PickerItem) => {
      switch (item.kind) {
        case 'aline-ai':
          selectAlineAi();
          return;
        case 'documents-empty':
          return;
        case 'entity':
          selectSearchResult(item.result);
          return;
        case 'email':
          if (!item.disabled) selectEmail(item.email);
          return;
        case 'intake-form':
          selectIntakeForm();
          return;
        case 'category':
        case 'view-all':
          openDrilldown(item.type);
      }
    },
    [openDrilldown, selectAlineAi, selectEmail, selectIntakeForm, selectSearchResult]
  );

  const clickItem = useCallback(
    (item: PickerItem) => {
      const selectableResult = getMultiSelectResultForItem(item);
      /**
       * Document, playbook, workflow, and report rows are check-off rows: clicking one builds up
       * the selection and Insert commits it. Users and action rows still act on the single click,
       * and Enter keeps its own path when nothing is checked.
       */
      if (selectableResult) {
        toggleEntitySelection(selectableResult);
        return;
      }

      selectItem(item);
    },
    [selectItem, toggleEntitySelection]
  );

  const setHighlightedIndex = useCallback(
    (fn: (prevIndex: number) => number) => {
      const prevIndex = highlightedItemKey ? optionKeys.indexOf(highlightedItemKey) : -1;
      const newIndex = fn(prevIndex);
      setHighlightedItemKey(optionKeys[newIndex] ?? null);
    },
    [highlightedItemKey, optionKeys]
  );

  const fetchMoreResults = useCallback(async () => {
    if (!activeGroup?.pageInfo.nextCursor || loading) return;

    await fetchMore({
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          ...fetchMoreResult,
          searchMentionEntities: {
            ...fetchMoreResult.searchMentionEntities,
            groups: prev.searchMentionEntities.groups.map(group => {
              const nextGroup = fetchMoreResult.searchMentionEntities.groups.find(
                resultGroup => resultGroup.type === group.type
              );
              if (!nextGroup) return group;

              const existingIds = new Set(group.results.map(result => result.id));
              const newResults = nextGroup.results.filter(result => !existingIds.has(result.id));

              return {
                ...nextGroup,
                results: [...group.results, ...newResults],
              };
            }),
          },
        };
      },
      variables: {
        input: {
          ...queryInput,
          cursor: getMentionEntitySearchCursorInput(activeGroup.pageInfo.nextCursor),
        },
      },
    });
  }, [activeGroup?.pageInfo.nextCursor, fetchMore, loading, queryInput]);

  const stopPickerKeyboardEvent = useCallback((event: PickerKeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if ('nativeEvent' in event) {
      event.nativeEvent.stopImmediatePropagation();
      return;
    }
    event.stopImmediatePropagation();
  }, []);

  const handlePickerNavigationKeyDown = useCallback(
    (event: PickerKeyboardEvent) => {
      if (event.key === 'ArrowLeft' && activeType) {
        stopPickerKeyboardEvent(event);
        closeDrilldown();
        return true;
      }

      if (event.key === 'ArrowDown') {
        stopPickerKeyboardEvent(event);
        setHighlightedIndex(prev => (prev + 1) % Math.max(optionKeys.length, 1));
        return true;
      }

      if (event.key === 'ArrowUp') {
        stopPickerKeyboardEvent(event);
        setHighlightedIndex(
          prev => (prev - 1 + optionKeys.length) % Math.max(optionKeys.length, 1)
        );
        return true;
      }

      const highlighted = items.find(item => item.key === highlightedItemKey);
      /**
       * With entities checked off, the accept keys commit that selection rather than inserting
       * only the highlighted row. ArrowRight on a category / view-all row still drills down.
       */
      if (
        getShouldCommitPendingSelection({
          highlightedItem: highlighted,
          key: event.key,
          selectedCount: selectedEntities.length,
        })
      ) {
        stopPickerKeyboardEvent(event);
        insertSelectedEntities();
        return true;
      }

      if (event.key === 'Enter' || event.key === 'Tab' || event.key === 'ArrowRight') {
        if (!highlighted) return false;

        stopPickerKeyboardEvent(event);
        selectItem(highlighted);
        return true;
      }

      return false;
    },
    [
      activeType,
      closeDrilldown,
      highlightedItemKey,
      insertSelectedEntities,
      items,
      optionKeys.length,
      selectedEntities.length,
      selectItem,
      setHighlightedIndex,
      stopPickerKeyboardEvent,
    ]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (handlePickerNavigationKeyDown(event)) return;

      inputProps.onKeyDown(event);
    },
    [handlePickerNavigationKeyDown, inputProps]
  );

  useEffect(() => {
    const path = editor.api.findPath(element);
    if (!path) return;

    const point = editor.api.before(path);
    if (!point) return;

    const pointRef = editor.api.pointRef(point);
    insertPoint.current = pointRef.current;

    return () => {
      pointRef.unref();
    };
  }, [editor, element]);

  useEffect(() => {
    /**
     * Slate/Plate can observe navigation keys at the editor level before the hidden input keeps
     * focus, which cancels the mention input and closes the popper. Capture these keys at the
     * document level while the picker is mounted so keyboard navigation belongs to the picker.
     */
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (uploadOpenRef.current) return;
      handlePickerNavigationKeyDown(event);
    };

    document.addEventListener('keydown', handleDocumentKeyDown, true);

    return () => document.removeEventListener('keydown', handleDocumentKeyDown, true);
  }, [handlePickerNavigationKeyDown]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Let the upload dialog handle Escape itself instead of closing the mention input.
      if (uploadOpenRef.current) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      closeInputWithoutText();
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [closeInputWithoutText]);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (uploadOpenRef.current) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (anchorEl?.contains(target) || popperElement?.contains(target)) return;
      closeInputWithoutText();
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [anchorEl, popperElement, closeInputWithoutText]);

  useEffect(() => {
    if (!optionKeys.length) {
      setHighlightedItemKey(null);
      return;
    }

    const currentItemExists = highlightedItemKey && optionKeys.includes(highlightedItemKey);
    if (!currentItemExists) setHighlightedItemKey(optionKeys[0]);
  }, [highlightedItemKey, optionKeys]);

  return (
    <span {...attributes} ref={composeRefs<HTMLSpanElement>(setAnchorEl, attributes.ref)}>
      <span contentEditable={false}>
        <span>@</span>
        <span className={classes.inputSizer} data-value={searchText}>
          <input
            ref={inputRef}
            className={classes.hiddenInput}
            type="text"
            value={searchText}
            onBlur={inputProps.onBlur}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </span>
      </span>
      {children}
      <Portal>
        <Paper
          ref={setPopperElement}
          className={classes.root}
          elevation={8}
          style={popperStyles.popper}
          {...{ [ALINE_MARKDOWN_MENTION_MENU_ATTR]: true }}
          {...popperAttributes.popper}
        >
          <div className={classes.scrollBody} id={scrollableTargetId}>
            {activeType ? (
              <>
                <DrilldownHeader
                  type={activeType}
                  uploadAction={
                    <MentionUploadAction
                      allowFileUpload={Boolean(config.allowFileUpload)}
                      onUpload={() => setUploadOpen(true)}
                    />
                  }
                  onBack={closeDrilldown}
                />
                {loading && !items.length ? (
                  <SkeletonRows />
                ) : !error && !items.length ? (
                  <DrilldownEmptyState type={activeType} />
                ) : activeGroup ? (
                  <InfiniteScroll
                    dataLength={activeGroup.results.length}
                    hasMore={activeGroup.pageInfo.hasMore}
                    loader={<LoadingRow />}
                    next={fetchMoreResults}
                    scrollableTarget={scrollableTargetId}
                  >
                    <PickerItems
                      highlightedItemKey={highlightedItemKey}
                      items={items}
                      selectedEntityKeys={selectedEntityKeys}
                      showHeaders={false}
                      onHighlight={setHighlightedItemKey}
                      onSelect={clickItem}
                      onToggleEntity={toggleEntitySelection}
                    />
                  </InfiniteScroll>
                ) : (
                  <PickerBody
                    error={Boolean(error)}
                    highlightedItemKey={highlightedItemKey}
                    items={items}
                    loading={loading}
                    query={mentionSearchText}
                    selectedEntityKeys={selectedEntityKeys}
                    onHighlight={setHighlightedItemKey}
                    onSelect={clickItem}
                    onToggleEntity={toggleEntitySelection}
                  />
                )}
              </>
            ) : (
              <PickerBody
                error={Boolean(error)}
                highlightedItemKey={highlightedItemKey}
                items={items}
                loading={loading}
                query={mentionSearchText}
                selectedEntityKeys={selectedEntityKeys}
                uploadAction={
                  <MentionUploadAction
                    allowFileUpload={Boolean(config.allowFileUpload)}
                    onUpload={() => setUploadOpen(true)}
                  />
                }
                onHighlight={setHighlightedItemKey}
                onSelect={clickItem}
                onToggleEntity={toggleEntitySelection}
              />
            )}
          </div>
          {showSelectionFooter && (
            <SelectionFooter
              selectedCount={selectedEntities.length}
              onInsert={insertSelectedEntities}
            />
          )}
        </Paper>
      </Portal>
      {config.allowFileUpload && (
        <UploadFilesPicker
          domContainer={FileStackDomContainer.ChatContext}
          open={uploadOpen}
          pickerOpts={{
            accept: [...aiChatAcceptedMimeTypes],
            afterUploadDone: data => {
              setUploadOpen(false);
              // Uploading from the picker keeps any entities already checked off instead of dropping them.
              insertMentionElements([
                ...getMentionNodesForResults(selectedEntities),
                ...(data.data?.createFiles ?? []).map(getDocumentMentionElementForUpload),
              ]);
            },
            maxFiles: MAX_CHAT_UPLOAD_FILES,
            onClose: () => setUploadOpen(false),
            props: {
              documentVariant: Apollo.DocumentVariant.Document,
              isSigned: false,
            },
          }}
        />
      )}
    </span>
  );
}

function PickerBody({
  error,
  highlightedItemKey,
  items,
  loading,
  onHighlight,
  onSelect,
  onToggleEntity,
  query,
  selectedEntityKeys,
  uploadAction,
}: {
  error: boolean;
  highlightedItemKey: string | null;
  items: PickerItem[];
  loading: boolean;
  onHighlight: (key: string) => void;
  onSelect: (item: PickerItem) => void;
  onToggleEntity: (result: MultiSelectSearchResult) => void;
  query: string;
  selectedEntityKeys: Set<string>;
  uploadAction?: React.ReactNode;
}) {
  const { classes } = useStyles();

  if (!items.length) {
    if (loading) return <SkeletonRows />;

    return (
      <Typography className={classes.emptyState} color="textSecondary" variant="body2">
        {error
          ? 'Could not load mentions. Try again.'
          : query.trim()
            ? 'No mentions match your search.'
            : 'No mentions available.'}
      </Typography>
    );
  }

  return (
    <PickerItems
      highlightedItemKey={highlightedItemKey}
      items={items}
      selectedEntityKeys={selectedEntityKeys}
      uploadAction={uploadAction}
      onHighlight={onHighlight}
      onSelect={onSelect}
      onToggleEntity={onToggleEntity}
    />
  );
}

function SelectionFooter({
  onInsert,
  selectedCount,
}: {
  onInsert: () => void;
  selectedCount: number;
}) {
  const { classes } = useStyles();

  return (
    <div className={classes.footer}>
      <Typography color="textSecondary" variant="caption">
        {selectedCount ? `${selectedCount} selected` : ''}
      </Typography>
      <SmallAlineButton
        color="primary"
        disabled={!selectedCount}
        variant="contained"
        onClick={event => {
          event.stopPropagation();
          onInsert();
        }}
        onMouseDown={event => {
          // Keep focus on the mention input and avoid the outside-click close.
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        Insert
      </SmallAlineButton>
    </div>
  );
}

function SkeletonRows({ count = 5 }: { count?: number }) {
  const { classes } = useStyles();

  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={classes.item}>
          <div className={classes.itemContainer}>
            <Skeleton height={20} variant="rectangular" width={20} />
            <Skeleton className={classes.skeletonText} height={18} variant="text" />
          </div>
        </div>
      ))}
    </>
  );
}

function DrilldownEmptyState({ type }: { type: Apollo.MentionEntitySearchType }) {
  const { classes } = useStyles();

  return (
    <div className={classes.drilldownEmpty}>No matching {CATEGORY_LABELS[type].toLowerCase()}</div>
  );
}

function DrilldownHeader({
  onBack,
  type,
  uploadAction,
}: {
  onBack: () => void;
  type: Apollo.MentionEntitySearchType;
  uploadAction?: React.ReactNode;
}) {
  const { classes } = useStyles();

  return (
    <div className={classes.drilldownHeader}>
      <IconButton
        aria-label="Back to all mention categories"
        className={classes.drilldownHeaderButton}
        size="small"
        onClick={event => {
          event.stopPropagation();
          onBack();
        }}
        onMouseDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <ArrowLeft size={14} />
      </IconButton>
      {CATEGORY_LABELS[type]}
      {type === Apollo.MentionEntitySearchType.Document && uploadAction}
    </div>
  );
}

function PickerItems({
  highlightedItemKey,
  items,
  onHighlight,
  onSelect,
  onToggleEntity,
  selectedEntityKeys,
  showHeaders = true,
  uploadAction,
}: {
  highlightedItemKey: string | null;
  items: PickerItem[];
  onHighlight: (key: string) => void;
  onSelect: (item: PickerItem) => void;
  onToggleEntity: (result: MultiSelectSearchResult) => void;
  selectedEntityKeys: Set<string>;
  showHeaders?: boolean;
  uploadAction?: React.ReactNode;
}) {
  const { classes } = useStyles();
  const sections = groupItemsByHeader(items);

  return (
    <>
      {sections.map((section, index) => (
        /**
         * Each section is its own sticky container so only one header pins to the top
         * at a time: as a section scrolls away its header leaves with it and the next
         * section's header takes over, instead of every sticky header stacking.
         */
        <div key={section.header ?? `section-${index}`} className={classes.section}>
          {showHeaders && section.header && (
            <div className={classes.groupHeader}>
              {section.header}
              {section.header === CATEGORY_LABELS[Apollo.MentionEntitySearchType.Document] &&
                uploadAction}
            </div>
          )}
          {section.items.map(item => (
            <PickerItemRow
              key={item.key}
              highlightedItemKey={highlightedItemKey}
              item={item}
              selectedEntityKeys={selectedEntityKeys}
              onHighlight={onHighlight}
              onSelect={onSelect}
              onToggleEntity={onToggleEntity}
            />
          ))}
        </div>
      ))}
    </>
  );
}

function PickerItemRow({
  highlightedItemKey,
  item,
  onHighlight,
  onSelect,
  onToggleEntity,
  selectedEntityKeys,
}: {
  highlightedItemKey: string | null;
  item: PickerItem;
  onHighlight: (key: string) => void;
  onSelect: (item: PickerItem) => void;
  onToggleEntity: (result: MultiSelectSearchResult) => void;
  selectedEntityKeys: Set<string>;
}) {
  const { classes, cx } = useStyles();
  const selectableResult = getMultiSelectResultForItem(item);

  return (
    <div
      className={cx(
        getPickerItemClassName({
          classes,
          highlightedItemKey,
          item,
        }),
        selectableResult && classes.selectableItem
      )}
      onClick={event => {
        event.stopPropagation();
        if (getIsPickerItemDisabled(item)) return;
        onSelect(item);
      }}
      onMouseDown={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onMouseEnter={() => onHighlight(item.key)}
    >
      <PickerItemRowContent
        item={item}
        selectableResult={selectableResult}
        selectedEntityKeys={selectedEntityKeys}
        onToggleEntity={onToggleEntity}
      />
    </div>
  );
}

function PickerItemRowContent({
  item,
  onToggleEntity,
  selectableResult,
  selectedEntityKeys,
}: {
  item: PickerItem;
  onToggleEntity: (result: MultiSelectSearchResult) => void;
  selectableResult: MultiSelectSearchResult | null;
  selectedEntityKeys: Set<string>;
}) {
  const { classes } = useStyles();
  if (!selectableResult) return <MentionEntityRow item={item} />;

  return (
    <>
      <AlineCheckbox
        checked={selectedEntityKeys.has(getSelectedEntityKey(selectableResult))}
        className={classes.itemCheckbox}
        size="small"
        slotProps={{
          htmlInput: {
            'aria-label': `Select ${getSearchResultDisplayName(selectableResult)}`,
          },
        }}
        onChange={() => onToggleEntity(selectableResult)}
        onClick={event => event.stopPropagation()}
      />
      <div className={classes.selectableItemContent}>
        <MentionEntityRow item={item} />
      </div>
    </>
  );
}

function groupItemsByHeader(items: PickerItem[]) {
  const sections: { header: string | null; items: PickerItem[] }[] = [];

  for (const item of items) {
    const current = sections.at(-1);
    const header = getHeaderForItem(item);
    /**
     * A non-null header that differs from the current section starts a new one;
     * header-less items (e.g. "view all") stay grouped with the section above them.
     */
    if (header && current?.header !== header) {
      sections.push({ header, items: [item] });
    } else if (current) {
      current.items.push(item);
    } else {
      sections.push({ header: null, items: [item] });
    }
  }

  return sections;
}

function MentionEntityRow({ item }: { item: PickerItem }): JSX.Element {
  switch (item.kind) {
    case 'aline-ai':
      return <AlineAiMentionMenuItem />;
    case 'category':
      return <CategoryRow type={item.type} />;
    case 'documents-empty':
      return <DocumentsEmptyRow />;
    case 'email':
      return <EmailRow disabled={item.disabled} email={item.email} />;
    case 'entity':
      return <SearchResultRow result={item.result} />;
    case 'intake-form':
      return <IntakeFormRow />;
    case 'view-all':
      return <ViewAllRow type={item.type} />;
  }
}

function MentionUploadAction({
  allowFileUpload,
  onUpload,
}: {
  allowFileUpload: boolean;
  onUpload: () => void;
}): JSX.Element | null {
  if (!allowFileUpload) return null;

  return <UploadButton onClick={onUpload} />;
}

function UploadButton({ onClick }: { onClick: () => void }): JSX.Element {
  const { classes } = useStyles();
  return (
    <SmallAlineButton
      className={classes.uploadButton}
      color="primary"
      variant="outlined"
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
      onMouseDown={event => {
        // Keep focus on the mention input and avoid the outside-click close.
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      Upload
    </SmallAlineButton>
  );
}

function AlineAiMentionMenuItem(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.itemContainer}>
      <div className={classes.mentionIcon}>
        <Sparkle size={14} strokeWidth={2} />
      </div>
      <div className={classes.itemText}>
        <Typography className={classes.title}>{ALINE_AI_OPTION_LABEL}</Typography>
      </div>
    </div>
  );
}

function DocumentsEmptyRow(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.itemContainer} style={{ justifyContent: 'center' }}>
      <Typography align="center" className={classes.title} color="textSecondary" variant="body2">
        No documents yet. Upload to get started.
      </Typography>
    </div>
  );
}

function IntakeFormRow(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.itemContainer}>
      <span className={classes.rowIcon}>
        <ClipboardList size={15} />
      </span>
      <div className={classes.itemText}>
        <Typography className={classes.title}>{INTAKE_FORM_OPTION_LABEL}</Typography>
      </div>
    </div>
  );
}

function CategoryRow({ type }: { type: Apollo.MentionEntitySearchType }): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.categoryItemContainer}>
      <div className={classes.itemContainer}>
        <EntityTypeIcon type={type} />
        <Typography className={classes.title}>{CATEGORY_LABELS[type]}</Typography>
      </div>
      <ChevronRight className={classes.rowIcon} size={16} />
    </div>
  );
}

function ViewAllRow({ type }: { type: Apollo.MentionEntitySearchType }): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.categoryItemContainer}>
      <div className={classes.itemContainer}>
        <EntityTypeIcon type={type} />
        <Typography className={classes.title}>
          View all {CATEGORY_LABELS[type].toLowerCase()}...
        </Typography>
      </div>
      <ChevronRight className={classes.rowIcon} size={16} />
    </div>
  );
}

function SearchResultRow({ result }: { result: SearchResult }): JSX.Element {
  if (result.__typename === 'User') return <UserRow user={result} />;
  return <EntityRow result={result} />;
}

function UserRow({ user }: { user: Extract<SearchResult, { __typename: 'User' }> }) {
  const { classes } = useStyles();
  return (
    <div className={classes.itemContainer}>
      <AlineAvatar avatarInfo={user} styles={{ size: 'xs' }} />
      <div className={classes.itemText}>
        <Typography className={classes.title}>{user.displayName}</Typography>
      </div>
    </div>
  );
}

function EmailRow({ disabled, email }: { disabled: boolean; email: string }) {
  const { classes, cx } = useStyles();
  return (
    <div className={classes.itemContainer}>
      <span className={cx(classes.plusIcon, disabled && classes.plusIconDisabled)}>
        <Plus size={14} strokeWidth={2.5} />
      </span>
      <div className={classes.itemText}>
        <Typography className={cx(classes.title, disabled && classes.emailTitleDisabled)}>
          {email}
        </Typography>
      </div>
    </div>
  );
}

function getPickerItemClassName({
  classes,
  highlightedItemKey,
  item,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  highlightedItemKey: string | null;
  item: PickerItem;
}) {
  if (getIsPickerItemDisabled(item)) return classes.disabledItem;
  if (item.key === highlightedItemKey) return classes.highlightedItem;
  return classes.item;
}

function getIsPickerItemDisabled(item: PickerItem) {
  if (item.kind === 'documents-empty') return true;

  return item.kind === 'email' && item.disabled;
}

function EntityRow({ result }: { result: Exclude<SearchResult, { __typename: 'User' }> }) {
  const { classes } = useStyles();
  const display = getResultDisplay(result);
  return (
    <div className={classes.itemContainer}>
      {result.__typename === 'Document' ? (
        <StorableIcon iconProps={{ height: 18, width: 18 }} storable={result} />
      ) : (
        display.icon
      )}
      <div className={classes.itemText}>
        <Typography className={classes.title}>{display.title}</Typography>
      </div>
    </div>
  );
}

function EntityTypeIcon({ type }: { type: Apollo.MentionEntitySearchType }) {
  const { classes } = useStyles();
  const icon = {
    [Apollo.MentionEntitySearchType.Document]: <FileText size={15} />,
    [Apollo.MentionEntitySearchType.Playbook]: <BookOpenText size={15} />,
    [Apollo.MentionEntitySearchType.Report]: <Table2 size={15} />,
    [Apollo.MentionEntitySearchType.User]: <UserRound size={15} />,
    [Apollo.MentionEntitySearchType.Workflow]: <Workflow size={15} />,
  } satisfies Record<Apollo.MentionEntitySearchType, React.ReactNode>;

  return <span className={classes.rowIcon}>{icon[type]}</span>;
}

function LoadingRow() {
  const { classes } = useStyles();
  return (
    <Typography className={classes.emptyState} color="textSecondary" variant="body2">
      Loading more...
    </Typography>
  );
}

function getHeaderForItem(item: PickerItem) {
  switch (item.kind) {
    case 'aline-ai':
    case 'email':
      return CATEGORY_LABELS[Apollo.MentionEntitySearchType.User];
    case 'documents-empty':
      return CATEGORY_LABELS[Apollo.MentionEntitySearchType.Document];
    case 'entity':
      return CATEGORY_LABELS[getTypeForResult(item.result)];
    case 'category':
    case 'intake-form':
      return 'More Mentions';
    case 'view-all':
      return null;
  }
}

function getTypeForResult(result: SearchResult) {
  switch (result.__typename) {
    case 'Document':
      return Apollo.MentionEntitySearchType.Document;
    case 'Playbook':
      return Apollo.MentionEntitySearchType.Playbook;
    case 'Report':
      return Apollo.MentionEntitySearchType.Report;
    case 'User':
      return Apollo.MentionEntitySearchType.User;
    case 'Workflow':
      return Apollo.MentionEntitySearchType.Workflow;
  }
}

function getQueryMode({
  activeType,
  query,
}: {
  activeType: Apollo.MentionEntitySearchType | null;
  query: string;
}) {
  if (activeType) return activeType;
  return query.trim() ? 'preview' : 'empty';
}

function getMentionInputSearchText(element: TElement, fallback: string) {
  const text = element.children
    .map(child => (TextApi.isText(child) ? child.text : ''))
    .join('')
    .replace(/^@/, '');

  return text || fallback;
}

function getMentionNodesForResults(results: SearchResult[]) {
  const mentionNodes: TMentionElement[] = [];
  for (const result of results) {
    const mentionNode = getMentionNodeForResult(result);
    if (mentionNode) mentionNodes.push(mentionNode);
  }

  return mentionNodes;
}

function getDocumentMentionElementForUpload(file: { id: string; name: string }) {
  const documentName = getDocumentMentionDisplayName(file.name);

  return {
    children: [{ text: '' }],
    documentId: file.id,
    documentName,
    mentionTypeName: 'document',
    type: mentionType,
    value: documentName,
  } satisfies DocumentMentionElement;
}

function getMentionNodeForResult(result: SearchResult): TMentionElement | null {
  if (result.__typename === 'Document') {
    const documentName = getDocumentMentionDisplayName(result.name);

    return {
      children: [{ text: '' }],
      documentId: result.id,
      documentName,
      mentionTypeName: 'document',
      type: mentionType,
      value: documentName,
    } satisfies DocumentMentionElement;
  }
  if (result.__typename === 'Playbook') {
    return {
      children: [{ text: '' }],
      mentionTypeName: 'playbook',
      playbookId: result.id,
      playbookName: result.title,
      type: mentionType,
      value: result.title,
    } satisfies PlaybookMentionElement;
  }
  if (result.__typename === 'Report') {
    return {
      children: [{ text: '' }],
      mentionTypeName: 'report',
      reportId: result.id,
      reportName: result.name,
      type: mentionType,
      value: result.name,
    } satisfies ReportMentionElement;
  }
  if (result.__typename === 'User') {
    return {
      children: [{ text: '' }],
      mentionTypeName: 'user',
      type: mentionType,
      userId: result.id,
      userName: result.displayName,
      value: result.displayName,
    } satisfies UserMentionElement;
  }
  if (result.__typename === 'Workflow') {
    return {
      children: [{ text: '' }],
      mentionTypeName: 'workflow',
      type: mentionType,
      value: result.name,
      workflowId: result.id,
      workflowName: result.name,
    } satisfies WorkflowMentionElement;
  }
  return null;
}

function getResultDisplay(result: Exclude<SearchResult, { __typename: 'User' }>) {
  if (result.__typename === 'Document') {
    return { icon: null, title: getDocumentMentionDisplayName(result.name) };
  }
  if (result.__typename === 'Playbook') {
    return {
      icon: <EntityTypeIcon type={Apollo.MentionEntitySearchType.Playbook} />,
      title: result.title,
    };
  }
  if (result.__typename === 'Report') {
    return {
      icon: <EntityTypeIcon type={Apollo.MentionEntitySearchType.Report} />,
      title: result.name,
    };
  }
  return {
    icon: <EntityTypeIcon type={Apollo.MentionEntitySearchType.Workflow} />,
    title: result.name,
  };
}
